import React from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  alpha
} from '@mui/material';

interface SectionProps {
  id?: string;
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  printTitle?: string;
  className?: string;
}

// Reusable sub-components for consistent styling
export const SubSectionTitle: React.FC<{ children: React.ReactNode; icon?: React.ReactNode }> = ({
  children,
  icon
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      {icon && (
        <Box sx={{
          color: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center'
        }}>
          {icon}
        </Box>
      )}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          letterSpacing: '-0.01em'
        }}
      >
        {children}
      </Typography>
    </Box>
  );
};

export const MetricHighlight: React.FC<{
  label: string;
  value: string | number;
  change?: { value: number; isPositive: boolean };
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}> = ({
  label,
  value,
  change,
  color = 'primary'
}) => {
  const theme = useTheme();

  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      backgroundColor: alpha(theme.palette[color].main, 0.05),
      border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
      textAlign: 'center'
    }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: theme.palette[color].main,
          mb: 0.5
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 500
        }}
      >
        {label}
      </Typography>
      {change && (
        <Typography
          variant="caption"
          sx={{
            color: change.isPositive ? theme.palette.success.main : theme.palette.error.main,
            fontWeight: 600,
            display: 'block',
            mt: 0.5
          }}
        >
          {change.isPositive ? '+' : ''}{change.value}%
        </Typography>
      )}
    </Box>
  );
};

export const Section: React.FC<SectionProps> = ({
  id,
  title,
  children,
  icon,
  printTitle,
  className
}) => {
  const theme = useTheme();

  return (
    <Paper
      id={id}
      className={`print-container ${className || ''}`}
      elevation={0}
      sx={{
        p: 4,
        mb: 4,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[2],
          transform: 'translateY(-1px)'
        },
        '@media print': {
          backgroundColor: '#ffffff !important',
          border: `1px solid #e0e0e0 !important`,
          borderRadius: '0 !important',
          boxShadow: 'none !important',
          transform: 'none !important',
          p: 3,
          mb: 3,
          pageBreakInside: 'avoid',
          '&:hover': {
            boxShadow: 'none !important',
            transform: 'none !important'
          }
        }
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          '@media print': {
            borderBottom: '2px solid #e0e0e0 !important',
            mb: 2,
            pb: 1.5
          }
        }}
      >
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              '@media print': {
                backgroundColor: 'transparent !important',
                border: `1px solid ${theme.palette.primary.main} !important`,
                color: `${theme.palette.primary.main} !important`
              }
            }}
          >
            {icon}
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              '@media print': {
                color: '#000000 !important',
                fontSize: '1.25rem !important'
              }
            }}
          >
            {printTitle || title}
          </Typography>
        </Box>
      </Box>

      {/* Section Content */}
      <Box sx={{
        '& .MuiTypography-root': {
          '@media print': {
            color: '#000000 !important'
          }
        },
        '& .MuiPaper-root': {
          '@media print': {
            backgroundColor: '#ffffff !important',
            boxShadow: 'none !important',
            border: '1px solid #e0e0e0 !important'
          }
        }
      }}>
        {children}
      </Box>
    </Paper>
  );
};